import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import { PageSelector, useAgGridPagination } from '@tassat/ag-grid';
import { ToolbarButton } from '@tassat/button';
import { SelectOption } from '@tassat/common-definitions';
import { StyledSelect } from '@tassat/formik-input';
import { CollateralHistoryTableOptions, adminCollateralHistoryTableOptionsInitialState } from '@tassat/fred-extended-definitions';
import { Export as ExportIcon } from '@tassat/icon';
import React from 'react';
import { SingleValue } from 'react-select';

import Header from 'Containers/header';
import TassatPayLogo from 'Images/TassatLogoAndName.svg';
import { getAccountPledgeTileData } from 'Lib/accountBalancesHelper';
import { TRANSACTIONS_PER_EXPORT } from 'Lib/constant';
import { caseInsensitiveSort } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import {
    haltRealtimePledgeAction,
    resetPledgeDetailAction,
    resumeRealtimePledgeAction
} from 'Reducers/actions/pledgesActions';
import { useAppDispatch, useAppSelector } from 'Reducers/store/hooks';
import { getAccountPositionPledgeAmountByFirmId } from 'Selectors/accountPositions';
import { getAllWalletsArray } from 'Selectors/accounts';
import { getHistoryExportAssets } from 'Selectors/assets';
import { getHaltRealtimePledges } from 'Selectors/common';
import { getFirmsArray } from 'Selectors/firms';
import { TableOptionsContext, tableOptionsReducer } from 'SharedComponents/contexts/TableOptionsContext';
import { useExports } from 'SharedComponents/hooks/useExports';
import PledgeDetailsModal from 'SharedComponents/modals/pledgeDetailsModal/pledgeDetailsModal';
import { PageHeaderTitle } from 'SharedComponents/pageHeader';
import CollateralHistoryAgGrid from 'SharedComponents/transactions/collateralHistoryAgGrid';

import WalletTotalBalanceTile from './WalletTotalBalanceTile/';

import type { CollateralProps } from '.';

export const PAGE_TITLE = 'Collateral Management';

export function Collateral(props: CollateralProps) {
    const { classes } = props;

    const realtimeHalted = useAppSelector(getHaltRealtimePledges);
    const accounts = useAppSelector(getAllWalletsArray);
    const firms = useAppSelector((state) => getFirmsArray(state, '', true));
    const assets = useAppSelector(getHistoryExportAssets);
    const dispatch = useAppDispatch();
    const haltRealtimePledges = () => dispatch(haltRealtimePledgeAction);
    const resumeRealtimePledges = () => dispatch(resumeRealtimePledgeAction);
    const resetPledgeDetail = () => dispatch(resetPledgeDetailAction);
    const theme = useTheme<ApplicationMuiTheme>();
    const [ selectedPledgeId, setSelectedPledgeId ] = React.useState<string>('');
    const [ rowCount, setRowCount ] = React.useState<number>(0);
    const [ TableOptions, TableOptionsDispatch ] = React.useReducer<(TableOptions, { type, values }: { type: string; values: object }) =>
    CollateralHistoryTableOptions>(tableOptionsReducer, { ...adminCollateralHistoryTableOptionsInitialState });
    const tableContextMemo = React.useMemo(() => {
        return {
            TableOptions,
            TableOptionsDispatch
        };
    }, [ TableOptions, TableOptionsDispatch ]);
    const [setPageNum] = useAgGridPagination(TableOptionsDispatch);
    const [ accountSelectOptions, setAccountSelectOptions ] = React.useState<SelectOption[]>([
        {
            label: 'All Accounts',
            value: 'none'
        }
    ]);
    const firmSelectOptions: SelectOption[] = React.useMemo(() => {
        firms.sort((firmA, firmB) => caseInsensitiveSort(firmA.Name, firmB.Name));

        const selectOptions = [
            {
                label: 'All Firms',
                value: 'none'
            }
        ];

        firms.forEach(firm => {
            if (firm.IsCollateral) {
                selectOptions.push({
                    label: firm.Name,
                    value: firm.FirmId
                });
            }
        });

        return selectOptions;
    }, [firms]);

    const {
        exportError,
        exportInFlight,
        setExportsContext
    } = useExports();

    React.useEffect(() => {
        if (props.location.state) {
            const firmId = props.location.state.firmId;
            const accountId = props.location.state.accountId;
            if (firmId) {
                const selectedFirm = firmSelectOptions.find(firm => firm.value === firmId);
                selectFirm(selectedFirm ?? null);
            }
            if (accountId) {
                const selectedAccount = accounts.find(account => account.AccountId === accountId);
                const firmAccounts = accounts
                    .filter(account => account.FirmId === TableOptions.SelectedFirm)
                    .sort((accountA, accountB) => caseInsensitiveSort(accountA.AccountName, accountB.AccountName))
                    .map(account => {
                        return {
                            label: account.AccountName,
                            value: account.Account
                        };
                    });
                setAccountSelectOptions(accountSelectOptions.slice(0, 1)
                    .concat(firmAccounts));
                selectAccount({
                    label: selectedAccount?.AccountName ?? '',
                    value: selectedAccount?.Account ?? ''
                });
            }
        }
    }, [ TableOptions.SelectedFirm, accounts ]);

    React.useEffect(() => {
        if (TableOptions.SelectedFirm !== 'none') {
            const firmAccounts = accounts
                .filter(account => account.FirmId === TableOptions.SelectedFirm)
                .sort((accountA, accountB) => caseInsensitiveSort(accountA.AccountName, accountB.AccountName))
                .map(account => {
                    return {
                        label: `${account.AccountName}`,
                        value: account.Account
                    };
                });
            setAccountSelectOptions(accountSelectOptions.slice(0, 1)
                .concat(firmAccounts));
        }
    }, [ TableOptions.SelectedFirm, accounts ]);

    React.useEffect(() => {
        if (!realtimeHalted && (TableOptions.FilterCondition || TableOptions.SelectedDateFilter === 'customRange')) {
            haltRealtimePledges();
        } else if (realtimeHalted && !TableOptions.FilterCondition && TableOptions.SelectedDateFilter !== 'customRange') {
            resumeRealtimePledges();
        }

        // TODO: Try to solve with some type of promise canceling in future
        // If we get filters from history state and they are not set yet
        // don't do a history request. If they are set, remove from history
        // state
        if (props.location.state) {
            if (props.location.state.firmId && TableOptions.SelectedFirm === 'none') {
                return;
            } else {
                const newState = props.location.state;
                delete newState.firmId;
                history.replaceState(newState, document.title);
            }

            if (props.location.state.accountId && TableOptions.SelectedAccount === 'none') {
                return;
            } else {
                const newState = props.location.state;
                delete newState.accountId;
                history.replaceState(newState, document.title);
            }
        }

        fetchCollateral();
    }, [
        TableOptions.DateCondition,
        TableOptions.FilterCondition,
        TableOptions.FirmCondition,
        TableOptions.AccountCondition,
        TableOptions.PageNo,
        TableOptions.Sort,
        TableOptions.SelectedDateFilter
    ]);

    React.useEffect(() => {
        if (props.location.state?.LinkTransRequestId) {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    ...adminCollateralHistoryTableOptionsInitialState,
                    FilterCondition: `RequestId|EQ|${props.location.state.LinkTransRequestId}`
                }
            });
            const lookupId = props.location.state.LinkTransRequestId + '-' + props.location.state.direction;
            setSelectedPledgeId(lookupId);
        }
    }, [props.location.state]);

    const selectFirm = (selectedFirmOption: SingleValue<SelectOption>) => {
        const selectedFirmId = selectedFirmOption?.value === 'none' ? '' : selectedFirmOption?.value;

        TableOptionsDispatch({
            type: 'update',
            values: {
                SelectedFirm: selectedFirmOption?.value,
                SelectedFirmName: selectedFirmId ? selectedFirmOption?.label : '',
                FirmCondition: selectedFirmId ? `PledgingFirmId|EQ|${selectedFirmId}^ReceivingFirmId|EQ|${selectedFirmId}` : '',
                SelectedAccount: 'none',
                AccountCondition: '',
                PageNo: 1
            }
        });
    };

    const selectAccount = (selectedAccountOption: SingleValue<SelectOption>) => {
        const selectedEOAAddress = selectedAccountOption?.value === 'none' ? '' : selectedAccountOption?.value;
        TableOptionsDispatch({
            type: 'update',
            values: {
                AccountCondition: selectedEOAAddress ? `PledgingAccountAddress|EQ|${selectedEOAAddress}^ReceivingAccountAddress|EQ|${selectedEOAAddress}` : '',
                SelectedAccount: selectedAccountOption?.value,
                PageNo: 1
            }
        });
    };

    const fetchCollateral = async (pageNum?) => {
        const {
            PageNo,
            RecordPerPage,
            Sort,
            DateCondition,
            FirmCondition,
            FilterCondition,
            AccountCondition
        } = TableOptions;

        const res = await Worker.getInstance()
            .getCollateral({
                Condition: `${!AccountCondition && FirmCondition ? `${FirmCondition}` : ''}${AccountCondition && `${AccountCondition}`}${FilterCondition && `${FilterCondition}${DateCondition ? ':' : ''}`}${DateCondition}`,
                Sort,
                PageNo: pageNum || PageNo,
                RecordPerPage
            }) || { data: undefined };

        setRowCount(res.data && res.data.TotalCount);

        return res;
    };

    const fetchCollateralExport = () => {
        const {
            Sort,
            DateCondition,
            FirmCondition,
            FilterCondition,
            AccountCondition
        } = TableOptions;

        let query = `&Condition=${!AccountCondition && FirmCondition ? `${FirmCondition}:` : ''}${AccountCondition && `${AccountCondition}:`}${FilterCondition && `${FilterCondition}${DateCondition ? ':' : ''}`}${DateCondition}`;
        query += `&Sort=${Sort}&RecordPerPage=${TRANSACTIONS_PER_EXPORT}`;

        setExportsContext((exportsContext) => ({
            ...exportsContext,
            totalChunks: Math.floor(rowCount / TRANSACTIONS_PER_EXPORT) + 1,
            chunkCounter: 0,
            exportInFlight: true,
            RecordType: 'admin-collateral',
            filterQuery: query,
            opts: { assets: assets as any }
        }));
    };

    const selectedAccount = TableOptions.SelectedAccount !== 'none' ? accounts.filter(acc => acc.Account === TableOptions.SelectedAccount)[0].AccountId : 'none';

    const accountPositionBalance = useAppSelector(
        (state) => getAccountPositionPledgeAmountByFirmId(state, TableOptions.SelectedFirm, selectedAccount)
    );

    const accountPositionStats = React.useMemo(() => {
        if (Object.keys(accountPositionBalance[0].security).length === 0) return;

        return getAccountPledgeTileData(accountPositionBalance);
    }, [accountPositionBalance]);

    return (
        <TableOptionsContext.Provider value={tableContextMemo}>
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Header />
                <PageHeaderTitle
                    title={PAGE_TITLE}
                />
                <Grid
                    className={classes.firmWalletSelectContainer}
                    container
                    direction='row'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.firmSelectContainer}
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={3}
                    >
                        Firms
                        <StyledSelect
                            className={classes.firmSelect}
                            defaultValue={firmSelectOptions[0]}
                            id='collateralFirmSelect'
                            onChange={selectFirm}
                            options={firmSelectOptions}
                            value={firmSelectOptions.find(opt => opt.value === TableOptions.SelectedFirm)}
                        />
                    </Grid>
                    {TableOptions.SelectedFirm !== 'none' &&
                        <Grid
                            container
                            direction='column'
                            item
                            spacing={0}
                            wrap='nowrap'
                            xs={3}
                        >
                            Accounts
                            <StyledSelect
                                className={classes.firmSelect}
                                defaultValue={accountSelectOptions[0]}
                                id='collateralAccountSelect'
                                onChange={selectAccount}
                                options={accountSelectOptions}
                                value={accountSelectOptions.find(opt => opt.value === TableOptions.SelectedAccount)}
                            />
                        </Grid>}
                    <Grid
                        item
                        xs={3}
                    >
                        {accountPositionStats && <WalletTotalBalanceTile stats={accountPositionStats} />}
                    </Grid>
                </Grid>
                <Grid
                    alignItems='center'
                    className={classes.exportContainer}
                    container
                    direction='row'
                    justifyContent='flex-end'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Typography
                        className={classes.realtimeTag}
                        variant='h5'
                    >
                        Real Time Updates
                        <span className={`${classes.realtimeState} ${realtimeHalted ? classes.realtimeDisabled : classes.realtimeEnabled}`}>{realtimeHalted ? 'OFF' : 'ON'}</span>
                    </Typography>
                    <ToolbarButton
                        actionInProgress={exportInFlight}
                        Icon={ExportIcon}
                        iconClassName={classes.exportIcon}
                        onClick={fetchCollateralExport}
                        text={exportError}
                    />
                </Grid>
                <Grid
                    className={classes.transactionGridContainer}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <CollateralHistoryAgGrid
                        setSelectedPledgeId={setSelectedPledgeId}
                        tableOptions={TableOptions}
                    />
                </Grid>
                <Grid
                    className={classes.pageSelectorContainer}
                    container
                    direction='row'
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                >
                    <PageSelector
                        changePageOnApplyOnly
                        currentPageNum={TableOptions.PageNo}
                        onPageChange={setPageNum}
                        totalRows={rowCount}
                    />
                    {theme?.app?.clientTheme.layoutSettings.useTassatPayLogoOnTransactionHistory &&
                        <img
                            className={classes.tassatPayLogo}
                            src={TassatPayLogo}
                        />}
                </Grid>
            </Grid>
            <PledgeDetailsModal
                onClose={() => {
                    setSelectedPledgeId('');
                    resetPledgeDetail();
                }}
                open={Boolean(selectedPledgeId)}
                selectedPledgeId={selectedPledgeId}
            />
        </TableOptionsContext.Provider>
    );
}